import React from 'react';
import { ActionButton, type ActionButtonData } from '../action-button/ActionButton';
import { t } from '../../services/locale';
import { ProfileBookingCourseSchedule } from './ProfileBookingCourseSchedule';
import type { FormatUnix, ProfileBooking, ProfileBranch } from './profileBooking.types';
import {
  getCourseDateFormated,
  getDateFormated,
  getEventDescription,
  getEventName,
  getTimeFormated,
  groupBookings,
  showCancelButton,
} from './profileBookingRules';

export type ProfileBookingsListData = {
  bookings?: ProfileBooking[];
  /** False until the host has finished the bookings request. */
  bookingsLoaded?: boolean;
  /** Unix seconds. Upcoming is strictly after this instant. */
  nowUnix?: number;
  branch?: ProfileBranch | null;
  formatUnix?: FormatUnix;
  autoBookingIsEnabled?: boolean;
  showContactAdminMessage?: boolean;
  isInteractiveSpotBookingEnabled?: boolean;
  onCancelBooking?: (booking: ProfileBooking) => void;
  getCancelActionData?: (booking: ProfileBooking) => ActionButtonData;
  getScheduleCloseActionData?: (booking: ProfileBooking) => ActionButtonData;
  ordinal?: (value: number) => string;
};

type Props = Readonly<{
  data?: ProfileBookingsListData;
}>;

type SectionKey = 'upcoming' | 'past';

const emptyFormatUnix: FormatUnix = () => '';

function sectionOrder(bookings: ProfileBooking[], nowUnix: number): SectionKey[] {
  const order: SectionKey[] = [];
  bookings.forEach((booking) => {
    const key: SectionKey = (booking.time_start ?? 0) > nowUnix ? 'upcoming' : 'past';
    if (!order.includes(key)) {
      order.push(key);
    }
  });
  return order;
}

function BookingStatus({
  booking,
  autoBookingIsEnabled,
  ordinal,
}: Readonly<{
  booking: ProfileBooking;
  autoBookingIsEnabled?: boolean;
  ordinal?: (value: number) => string;
}>) {
  if (booking.status === 'WAITING') {
    const place = autoBookingIsEnabled && booking.position != null && ordinal
      ? `${ordinal(booking.position)} `
      : '';
    return (
      <span className="list-text-left-status" id={`waiting-list-position-${booking._id ?? ''}`}>
        ({place}{t('WEB_ON_WAITING_LIST')})
      </span>
    );
  }

  if (booking.status === 'CANCELED') {
    return (
      <span className="list-text-left-status" id={`booking-cancelled-${booking._id ?? ''}`}>
        ({t('WEB_BOOKING_CANCELED')})
      </span>
    );
  }

  return null;
}

function SpotExtras({ booking }: Readonly<{ booking: ProfileBooking }>) {
  const showSpot = booking.status === 'BOOKED' && booking.isSpotBookingEnabled;
  const showFriends = (booking.guest_bookings ?? 0) > 0;
  if (!showSpot && !showFriends) {
    return null;
  }

  return (
    <div className="booking-spot-extras">
      {showSpot ? (
        <span className="spot-label">
          {booking.bookedSpotName
            ? t('WEB_BOOK_SPOT', { spot: booking.bookedSpotName })
            : t('WEB_BOOK_SPOT_UNASSIGNED')}
        </span>
      ) : null}
      {showFriends ? (
        <span className="friend-tag">
          {t('WEB_BOOK_FRIEND_COUNT', { count: booking.guest_bookings })}
        </span>
      ) : null}
    </div>
  );
}

function BookingRow({
  booking,
  isUpcoming,
  data,
  formatUnix,
}: Readonly<{
  booking: ProfileBooking;
  isUpcoming: boolean;
  data: ProfileBookingsListData;
  formatUnix: FormatUnix;
}>) {
  const canCancel = showCancelButton(isUpcoming, booking);
  const cancelData = canCancel ? data.getCancelActionData?.(booking) : undefined;
  const isCourse = booking.model === 'courses';

  return (
    <li id={`profile-booking-${booking._id ?? ''}`}>
      <div className="list-text-left">
        <h4 id={`profile-booking-${booking._id ?? ''}-title`}>
          {getEventName(booking)}{' '}
          <BookingStatus
            booking={booking}
            autoBookingIsEnabled={data.autoBookingIsEnabled}
            ordinal={data.ordinal}
          />
        </h4>
        <p>{getEventDescription(booking)}</p>
      </div>
      {canCancel && cancelData ? (
        <div className="list-button-right">
          <span className="profile-action-button">
            <ActionButton
              data={{
                ...cancelData,
                onClick: () => data.onCancelBooking?.(booking),
              }}
            />
          </span>
        </div>
      ) : null}
      {isCourse ? (
        <div className="list-text-right">
          <span>
            {getCourseDateFormated(booking, formatUnix)}
            <br />
          </span>
          <ProfileBookingCourseSchedule
            booking={booking}
            branch={data.branch}
            formatUnix={formatUnix}
            getCloseActionData={data.getScheduleCloseActionData}
          />
        </div>
      ) : (
        <div className="list-text-right">
          {getTimeFormated(booking, data.branch, formatUnix)}
          <br />
          {getDateFormated(booking, formatUnix)}
          {data.isInteractiveSpotBookingEnabled ? <SpotExtras booking={booking} /> : null}
        </div>
      )}
    </li>
  );
}

export function ProfileBookingsList({ data }: Props) {
  if (!data?.bookingsLoaded) {
    return null;
  }

  const formatUnix = data.formatUnix ?? emptyFormatUnix;
  const nowUnix = data.nowUnix ?? 0;
  const bookings = data.bookings ?? [];
  const grouped = groupBookings(bookings, nowUnix);

  if (grouped.isEmpty) {
    return (
      <ul className="list">
        <li>
          <span id="profile-bookings-empty-message" className="show-more-contact">
            {t('WEB_THERE_ARE_NO_BOOKINGS_YET')}
          </span>
        </li>
      </ul>
    );
  }

  const sections: Record<SectionKey, ProfileBooking[]> = {
    upcoming: grouped.upcoming,
    past: grouped.past,
  };

  return (
    <>
      {sectionOrder(bookings, nowUnix).map((key) => {
        const rows = sections[key];
        if (rows.length === 0) {
          return null;
        }
        return (
          <span key={key}>
            <div className="list-label">
              <span>{key === 'upcoming' ? t('WEB_UPCOMING') : t('WEB_PAST_BOOKINGS')}</span>
            </div>
            <ul className="list">
              {rows.map((booking, index) => (
                <BookingRow
                  key={booking._id ?? `${key}-${index}`}
                  booking={booking}
                  isUpcoming={key === 'upcoming'}
                  data={data}
                  formatUnix={formatUnix}
                />
              ))}
              {key === 'past' && data.showContactAdminMessage ? (
                <li>
                  <span id="profile-bookings-contact-admin-message" className="show-more-contact">
                    {t('WEB_CONTACT_ADMIN_TO_GET_OLDER_BOOKINGS')}
                  </span>
                </li>
              ) : null}
            </ul>
          </span>
        );
      })}
    </>
  );
}
